import { FunctionComponent } from 'react';

import { LocalAirport, PriorityHigh, HourglassFull, Search } from '../../components/icons';
import { Typography, IconButton, Card, Divider, Tooltip } from '../../tailwindcomponents';
import { LinePackets } from '../../types/line';
import { redirectToMxi } from '../../config/mxiProvider';
import { redirectToIdmrTask, redirectToIdmrWorkPackage } from '../../config/idmrProvider';
import { formatDueDate } from '../../utils/dateUtils';
import { isBoeingAircraft } from '../../utils/aircraftUtils';
import { palette } from '../../theme/palette';

interface LineWorkpackageCardProps {
    task: LinePackets,
    type: "LINE" | "BAY" | "BAY-USER"
    onOpenDialogClicked: Function,
}

const LineWorkpackageCard: FunctionComponent<LineWorkpackageCardProps> = ({ task, type, onOpenDialogClicked }) => {
    const isBoeingTask = isBoeingAircraft(task.inventory);
    // Paper (which Card wraps) sets `color` unconditionally via inline style, so a
    // `text-black/[0.87]` className can never win — the PAUSE branch's text color
    // must be passed via `style` instead.
    const cardBorderClass = task.status === "IN WORK" ? "border-0 border-solid border-r-[13px] border-r-accent-blue cursor-grab" : task.status === "COMPLETE" ? "border-0 border-solid border-r-[13px] border-r-status-complete cursor-grab" : task.status === "ACTV" ? "border-0 border-solid border-r-[13px] border-r-status-actv cursor-grab" : task.status === "PAUSE" ? "border-0 border-solid border-r-[13px] border-r-status-pause cursor-grab" : task.status === "COMMIT" ? "border-0 border-solid border-r-[13px] border-r-status-night cursor-grab" : "border-0 border-solid border-r-[13px] border-r-status-cancel cursor-grab";
    return (
        <Card className={cardBorderClass} style={task.status === "PAUSE" ? { color: 'rgba(0,0,0,0.87)' } : undefined}>
            {type === "LINE" &&
                <div className="flex flex-col" >
                    <div className="flex flex-row flex-wrap p-1" >
                        <div className="w-1/2">
                            <div className="flex flex-wrap">
                                <Typography text={"Name:"} variant="h5" margin={[1, 1, 0, 1]} align="left" />
                            </div>
                            <Typography fontWeight="bolder" text={task.wo_name} variant="h5" margin={[1, 1, 0, 1]} />
                        </div>
                        <div className="w-1/2">
                            <Typography text={"Due date:"} variant="h5" margin={[1, 1, 0, 2]} align="left" />
                            <Typography fontWeight="bold" text={formatDueDate(task.due_date)} variant="h5" margin={[1, 1, 0, 2]} align="left" />
                        </div>
                    </div>
                    <div className="flex flex-row flex-wrap p-1" >
                        <div className="md:w-1/6">
                            <Typography text={"Tail"} variant="h5" margin={[1, 1, 0, 1]} align="left" />
                            <Typography text={task.tail_number} color="primary" fontWeight="bolder" variant="h5" margin={[1, 1, 0, 1]} align="left" />
                        </div>
                        <div className="md:w-1/6">
                            <Typography text={"Mxi Start date:"} variant="h5" margin={[1, 1, 0, 1]} align="left" />
                            <Typography text={task.mxi_start_date_text} color="primary" fontWeight="bolder" variant="h5" margin={[1, 1, 0, 1]} align="left" />
                        </div>
                        <div className="md:w-1/4">
                            <Typography text={"Mxi End date:"} variant="h5" margin={[1, 1, 0, 1]} align="left" />
                            <Typography text={task.mxi_end_date_text} color='primary' fontWeight="bolder" variant="h5" margin={[1, 1, 0, 1]} align="left" />
                        </div>
                        <div className="md:w-1/4">
                            <Typography text={"Package duration :"} variant="h5" margin={[1, 1, 0, 1]} align="left" />
                            <Typography text={task.package_duration_text} color='primary' fontWeight="bolder" variant="h5" margin={[1, 1, 0, 1]} align="left" />
                        </div>
                        <div className="flex flex-row flex-nowrap gap-2 items-center">
                            <div>
                                <IconButton onClick={() => redirectToMxi(task.wo_barcode)} className="text-[10px] px-1 w-auto">MXI</IconButton>
                            </div>
                            {(task.status !== "COMPLETE" && task.is_unassigned === false && !isBoeingTask) &&
                                <div>
                                    <IconButton onClick={() => redirectToIdmrWorkPackage(task.wo_barcode)} className="text-[10px] px-1 w-auto">eSign</IconButton>
                                </div>
                            }
                        </div>
                    </div>
                    {(task.is_task_critical || task.is_on_hold || task.is_on_time === false) && <div className="flex flex-wrap">
                        {task.is_task_critical && <div className="w-1/3 flex flex-wrap justify-center">
                            <Tooltip title={'Critical Work'} type='primary'><PriorityHigh color={'error'} /></Tooltip>
                        </div>}
                        {task.is_on_hold && <div className="w-1/3 flex flex-wrap justify-center">
                            <Tooltip title={'On hold'} type='primary' placement="top-start"><HourglassFull color={'error'} /></Tooltip>
                        </div>}
                        {task.is_on_time === false && <div className="w-1/3 flex flex-wrap justify-center">
                            <Tooltip title={'Aircraft is not on time, Reschedule'} type='primary' placement="top-start"><LocalAirport color={'error'} /></Tooltip>
                        </div>}
                    </div>}
                </div>
            }
            {
                type === "BAY" &&
                <div className="flex flex-col" >
                    <div className="flex flex-row flex-wrap p-1" >
                        <div className="w-1/3 flex flex-wrap justify-center">
                            <div className="w-full flex flex-wrap justify-center"><Typography text={"Mxi Start date:"} variant="h5" margin={[1, 1, 0, 1]} align="left" /></div>
                            <Typography text={task.mxi_start_date_text} color="primary" fontWeight="bolder" variant="h5" margin={[1, 1, 0, 1]} align="left" />
                        </div>
                        <div className="w-1/3 flex flex-wrap justify-center">
                            <div className="w-full flex flex-wrap justify-center"><Typography text={"Mxi End date:"} variant="h5" margin={[1, 1, 0, 1]} align="left" /></div>
                            <Typography text={task.mxi_end_date_text} color='primary' fontWeight="bolder" variant="h5" margin={[1, 1, 0, 1]} align="left" />
                        </div>
                        <div className="flex flex-row flex-nowrap justify-start gap-2 items-center">
                            <div>
                                <IconButton onClick={() => redirectToMxi(task.wo_barcode)} className="text-[10px] px-1 w-auto">MXI</IconButton>
                            </div>
                            {(task.status !== "COMPLETE" && task.is_unassigned === false && !isBoeingTask) &&
                                <div>
                                    <IconButton onClick={() => redirectToIdmrTask(task.wo_barcode, task.task_barcode)} className="text-[10px] px-1 w-auto">eSign</IconButton>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            }

            {
                type === "BAY-USER" &&
                <div className="p-3">
                    {/* Header: Tail + WO Name + Start date (highlighted) */}
                    <div className="flex items-start justify-between mb-1">
                        <div className="flex flex-col gap-0.5">
                            <span className="text-[1.4rem] font-[800] text-accent-blue tracking-[0.5px]">{task.tail_number}</span>
                            <span className="text-base font-bold text-content">Wo name: {task.wo_name}</span>
                            <span className="text-base font-bold text-[#1565c0]">Start: {task.mxi_start_date_text}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <IconButton
                                className="text-[10px] font-semibold px-1 w-auto rounded border border-solid border-line hover:bg-black/[0.04]"
                                style={{ color: palette.accentBlue }}
                                onClick={() => redirectToMxi(task.wo_barcode)}
                            >
                                WO MXi
                            </IconButton>
                            {(task.status !== "COMPLETE" && task.is_unassigned === false && !isBoeingTask) &&
                                <IconButton
                                    className="text-[10px] font-semibold px-1 w-auto rounded border border-solid border-line hover:bg-black/[0.04]"
                                    style={{ color: palette.accentBlue }}
                                    onClick={() => redirectToIdmrWorkPackage(task.wo_barcode)}
                                >
                                    WO eSign
                                </IconButton>
                            }
                            <IconButton
                                aria-label="search"
                                className="p-1"
                                onClick={() => onOpenDialogClicked(task.task_barcode)}
                            >
                                <Search color="primary" fontSize="small" />
                            </IconButton>
                        </div>
                    </div>

                    <Divider className="my-1.5 bg-[color:var(--ds-border)]" />

                    {/* Secondary info grid */}
                    <div className="grid grid-cols-2 gap-y-1 gap-x-4 mb-1.5">
                        <div className="flex flex-col">
                            <span className="text-content text-xs font-medium uppercase tracking-[0.5px] mb-0.5">Task</span>
                            <span className="text-xs font-medium text-content">{task.task_name}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-content text-xs font-medium uppercase tracking-[0.5px] mb-0.5">Status</span>
                            <span className="inline-block text-xs font-bold py-px px-2 rounded bg-black/[0.08]">{task.status}</span>
                        </div>
                    </div>

                    {/* Assigned Labour */}
                    <div className="mt-1 p-1.5 bg-black/[0.04] rounded">
                        <span className="text-xs font-semibold uppercase tracking-[0.5px] text-content mb-0.5">Assigned Labour</span>
                        <div className="text-[0.8rem] font-medium text-accent-blue">{task.assigned_employees}</div>
                    </div>

                    <Divider className="my-1.5 bg-[color:var(--ds-border)]" />

                    {/* Actions */}
                    <div className="flex gap-1 mt-1">
                        <IconButton
                            className="text-[10px] font-semibold px-1 w-auto rounded border border-solid border-line hover:bg-black/[0.04]"
                            style={{ color: palette.accentBlue }}
                            onClick={() => redirectToMxi(task.task_barcode)}
                        >
                            MXI
                        </IconButton>
                        {(task.status !== "COMPLETE" && task.is_unassigned === false && !isBoeingTask) &&
                            <IconButton
                                className="text-[10px] font-semibold px-1 w-auto rounded border border-solid border-line hover:bg-black/[0.04]"
                                style={{ color: palette.accentBlue }}
                                onClick={() => redirectToIdmrTask(task.wo_barcode, task.task_barcode)}
                            >
                                eSign
                            </IconButton>
                        }
                    </div>
                </div>
            }
        </Card>
    )
}

export default LineWorkpackageCard;